import React, { useState } from 'react'
import { useTheme } from '@/hooks/useTheme'

interface TermsAndConditionsModalProps {
  isOpen: boolean
  onClose: () => void
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ isOpen, onClose }) => {
  const { colors } = useTheme()
  const [isLoading, setIsLoading] = useState(false)

  const handleDownloadPDF = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/espacios/reglamento/descargar`)
      if (!response.ok) {
        throw new Error('Error al descargar el reglamento')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'Reglamento-Camping-Niklitschek.pdf'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error descargando PDF:', error)
      alert('No pudimos descargar el reglamento. Intenta nuevamente.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between"
          style={{ backgroundColor: `${colors.primary}15` }}
        >
          <h2 className="text-2xl font-bold" style={{ color: colors.primary }}>
            Términos y Condiciones
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4 text-gray-700">
            <section>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.primaryDark }}>
                Reglamento General - Camping
              </h3>
              <p className="text-sm mb-3">
                Bienvenido a Camping Playa Niklitschek. Te invitamos a leer atentamente el siguiente reglamento que
                aplica a todos nuestros huéspedes. Al hacer una reserva, aceptas cumplir con todos los términos y
                condiciones establecidos en este documento.
              </p>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                1. Check-in y Check-out
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li><strong>Check-in campistas:</strong> Desde las 13:00 hasta las 19:00. Si llega después de las 19:00, avisar al administrador</li>
                <li><strong>Check-out campistas:</strong> Hasta las 12:00. Consultar disponibilidad para extensiones</li>
                <li><strong>Check-in cabañas:</strong> Desde las 15:00</li>
                <li><strong>Check-out cabañas:</strong> Hasta las 11:00</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                2. Capacidad
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Camping: Máximo 8 personas por sitio (incluyendo mesones)</li>
                <li>Cabañas: Máximo 6 personas</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                3. Estacionamiento
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Se permite un vehículo por sitio, salvo acuerdo especial</li>
                <li>Los vehículos deben estacionarse únicamente en áreas designadas</li>
                <li>Prohibido estacionar en zonas no autorizadas</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                4. Ruido y Horarios de Tranquilidad
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Horario de silencio: <strong>23:30 a 08:00</strong> para garantizar el descanso</li>
                <li>No se permiten aparatos de música con volumen alto ni ruidos molestos</li>
                <li><strong>No se permiten fiestas</strong></li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                5. Fuego y Fogatas
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Fogatas solo en lugares designados (parrillas)</li>
                <li>Prohibido encender fuego en áreas no autorizadas o dentro de carpas</li>
                <li>Al usar fogata, responsable de apagarla completamente antes de irse o dormir con abundante agua</li>
                <li>Asegurarse de que no queden brasas vivas</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                6. Basura y Reciclaje
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Desechar basura en contenedores adecuados</li>
                <li>Prohibido dejar basura esparcida, enterrarla o arrojarla en playa/lago</li>
                <li>No arrojar colillas al suelo - usar recipientes para cenizas o basureros</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                7. Seguridad y Responsabilidad
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Los visitantes utilizan el camping bajo su propio riesgo</li>
                <li>El camping no se hace responsable por pérdidas, robos o daños personales, salvo negligencia grave demostrable</li>
                <li>Respetar instrucciones del personal administrativo y de seguridad</li>
                <li>En caso de emergencia (fuego, accidente, salud), notificar inmediatamente al personal</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                8. Instalaciones Comunes
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Baños, duchas, lavaplatos y áreas comunes deben usarse con respeto y mantenerse limpios</li>
                <li>No reservar indebidamente instalaciones comunes por largos periodos sin permiso</li>
                <li>Si algo está roto, sucio o dañado, avisar al equipo de administración</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                9. Animales de Compañía
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li><strong>No se aceptan mascotas</strong></li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                10. Normativa Legal y Cumplimiento
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Al reservar y pagar, el visitante declara conocer y aceptar este reglamento</li>
                <li>El camping se reserva el derecho de modificar este reglamento previo aviso</li>
                <li>Se aplicará la normativa chilena aplicable a recintos de campamento (DS 701 del Sernatur)</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                11. Reglamento para Mesones por el Día
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Uso de mesones: 09:00 a 20:00, salvo acuerdo con administración</li>
                <li>Capacidad máxima: 8 personas por mesón</li>
                <li>Limpiar mesón al finalizar (retirar basura, platos, restos de comida, botellas)</li>
                <li>Estacionamiento frente a mesones: uso exclusivo de clientes con mesón arrendado</li>
                <li><strong>Prohibido:</strong> uso de enchufes para asadores con motor y artefactos de alto consumo (traer fuente de energía propia)</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                12. Reglamento Sitios de Camping (sin baño privado)
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Carpas dentro de límites señalados</li>
                <li>Máximo 8 personas por sitio</li>
                <li>Check-in: desde las 13:00 / Check-out: hasta las 11:30</li>
                <li>No desalojar en horario = cobro de noche extra + abandono inmediato</li>
                <li>Un vehículo permitido por sitio</li>
                <li>Uso responsable de enchufes y agua</li>
                <li>Baños y duchas: usar adecuadamente, respetar orden e higiene</li>
                <li>Prohibidas brasas fuera de espacios habilitados</li>
                <li><strong>Prohibido:</strong> enchufes para asadores con motor y artefactos de alto consumo</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                13. Reglamento Sitios de Camping con Baño Privado
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Baño y ducha privada: exclusivo para el grupo del sitio asignado</li>
                <li>Posibles revisiones periódicas del estado del baño</li>
                <li>Check-in: desde las 13:00 / Check-out: hasta las 11:30</li>
                <li>No desalojar en horario = cobro de noche extra + abandono inmediato</li>
                <li>Agua caliente/calefacción: seguir instrucciones para uso adecuado</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                14. Reglamento Cabañas
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Capacidad: Máximo 6 personas (ampliable a 8 contactando administración)</li>
                <li>Check-in: desde las 15:00 / Check-out: hasta las 11:00</li>
                <li>No desalojar en horario = cobro de noche extra + abandono inmediato</li>
                <li>Las cabañas deben dejarse limpias</li>
                <li>Respetar instalaciones (muebles, electrodomésticos)</li>
                <li>Respetar horario de silencio y no fumar dentro</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                15. Reglamento Playa
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>No arrojar basura en la playa ni en el lago</li>
                <li>Nadar en zonas accesibles y seguras</li>
                <li>Comportamiento respetuoso con otros usuarios</li>
                <li>No molestar fauna ni flora local</li>
                <li>Notificar emergencias al personal del camping</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                16. Derecho de Admisión
              </h4>
              <p className="text-sm mb-2">El camping se reserva el derecho de admisión y puede negar la entrada a quienes no respeten el reglamento.</p>
              <p className="text-sm font-semibold mb-1">Sanciones y Medidas:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Primera advertencia verbal: Corrección de conducta</li>
                <li>Advertencia escrita/registro: Para infracciones serias</li>
                <li>Desalojo: Por reincidencia o infracción grave</li>
                <li>Prohibición futura: Negar futuras reservas por incumplimiento grave</li>
              </ul>
              <p className="text-sm font-semibold mt-2 mb-1">Protocolo de Desalojo:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Documentación: registrar fecha, hora y descripción de infracción</li>
                <li>Notificación: informar verbalmente y/o por escrito</li>
                <li>Plazo de retiro: 30 a 60 minutos para recoger pertenencias</li>
                <li>Acompañamiento: supervisión del proceso por el personal</li>
                <li>Registro final: elaborar informe interno y aplicar cargos si corresponde</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold mb-2" style={{ color: colors.primary }}>
                17. Políticas de Reserva
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Cancelación con <strong>7 o más días</strong> de anticipación: <strong>reembolso del 100%</strong></li>
                <li>Cancelación con <strong>3 días</strong> de anticipación: <strong>reembolso del 50%</strong></li>
                <li>Menos de 3 días antes de la reserva: <strong>solo se permite cambio de fecha</strong></li>
              </ul>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-4"
          style={{ backgroundColor: `${colors.primary}10` }}
        >
          <button
            onClick={handleDownloadPDF}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            {isLoading ? '⏳ Descargando...' : '📥 Descargar PDF'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-semibold transition-all"
            style={{
              backgroundColor: '#e5e7eb',
              color: '#374151'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d1d5db'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb'
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default TermsAndConditionsModal
